import React, { useState, useEffect } from "react";
import SafetyScoreAPI from "../../services/SafetyScoreAPI";
import MemberViewAPI from "../../services/MemberViewAPI";
import { useUser } from "../../context/UserContext";
import RefreshButton from "../../common/RefreshButton";
import LoadingSpinner from "../../common/LoadingSpinner";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "../css/SafetyScoreCard.css";

function SafetyScoreCard({ setHeroContent }) {
  const { t } = useTranslation();
  const { isReadOnlyView, selectedUser } = useUser();
  const [safetyData, setSafetyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);

  useEffect(() => {
    fetchSafetyScore(true);
  }, [isReadOnlyView, selectedUser?.uuid]);

  const fetchSafetyScore = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      let response;

      if (isReadOnlyView && selectedUser?.uuid) {
        // Owner viewing a consented member — use owner-access proxy endpoint
        response = await MemberViewAPI.getMemberSafetyScore(selectedUser.uuid);
        if (response && response.success && response.data) {
          // Normalize to the shape SafetyScoreCard expects
          setSafetyData({
            totalSafetyScore: response.data.totalScore,
            safetyLevel: response.data.safetyLevel,
            criterionBreakdown: response.data.criterionBreakdown || [],
          });
        } else {
          throw new Error("Unable to retrieve member safety score");
        }
        return;
      }

      if (forceRefresh) {
        setIsRefreshing(true);
        // Calculate fresh score from scratch
        response = await SafetyScoreAPI.calculateUserSafetyScore();
      } else {
        // Use cached score if available (for subsequent fetches within session)
        try {
          response = await SafetyScoreAPI.getUserSafetyScore();
        } catch (getError) {
          console.log("[SafetyScoreCard] No cached score, calculating...");
          response = await SafetyScoreAPI.calculateUserSafetyScore();
        }
      }

      if (response && response.success && response.data) {
        setSafetyData(response.data);
      } else {
        throw new Error("Unable to retrieve safety score");
      }
    } catch (error) {
      console.error("[SafetyScoreCard] Error fetching safety score:", error);
      setError(error.message || "Failed to load safety score");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchSafetyScore(true);
  };

  // Component display config with colors (like iPhone storage categories)
  const componentConfig = {
    login_activity: {
      name: t("dashboard.safetyScore.comp.appParticipation"),
      color: "#3b82f6", // Blue
      formatLabel: (total, success) => {
        if (total === 0) return t("dashboard.safetyScore.noDataYet");
        if (success >= total) return t("dashboard.safetyScore.active");
        return t("dashboard.safetyScore.daysActive")
          .replace("{success}", success)
          .replace("{total}", total);
      },
    },
    email_simulations: {
      name: t("dashboard.safetyScore.comp.emailSafety"),
      color: "#10b981", // Green
      formatLabel: (total, success) => {
        if (total === 0) return t("dashboard.safetyScore.noTestsYet");
        return t("dashboard.safetyScore.avoidedOf")
          .replace("{success}", success)
          .replace("{total}", total);
      },
    },
    sms_simulations: {
      name: t("dashboard.safetyScore.comp.textSafety"),
      color: "#06b6d4", // Cyan/Teal
      formatLabel: (total, success) => {
        if (total === 0) return t("dashboard.safetyScore.noTestsYet");
        return t("dashboard.safetyScore.avoidedOf")
          .replace("{success}", success)
          .replace("{total}", total);
      },
    },
    training_completion: {
      name: t("dashboard.safetyScore.comp.training"),
      color: "#8b5cf6", // Purple
      formatLabel: (total, success) => {
        if (total === 0) return t("dashboard.safetyScore.noneAvailable");
        if (success >= total) return t("dashboard.safetyScore.allComplete");
        return t("dashboard.safetyScore.doneOf")
          .replace("{success}", success)
          .replace("{total}", total);
      },
    },
  };

  // Display order for components
  const displayOrder = [
    "login_activity",
    "training_completion",
    "sms_simulations",
    "email_simulations",
  ];

  if (loading && !safetyData) {
    return (
      <div className="simple-card safety-score-card">
        <h2 className="simple-card-title">
          {t("dashboard.safetyScore.title")}
        </h2>
        <LoadingSpinner
          message={t("common.loading")}
          size="md"
          className="compact"
        />
      </div>
    );
  }

  if (error && !safetyData) {
    return (
      <div className="simple-card safety-score-card">
        <h2 className="simple-card-title">
          {t("dashboard.safetyScore.title")}
        </h2>
        <div className="safety-error-message">
          <p className="safety-error-text">{t("common.unableToLoad")}</p>
          <button
            onClick={() => fetchSafetyScore()}
            className="safety-retry-btn"
          >
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  const totalScore = Math.round(safetyData?.totalSafetyScore || 0);
  const breakdown = safetyData?.criterionBreakdown || [];

  // Build ordered components with their weighted scores
  const components = displayOrder
    .map((criterionName) => {
      const criterion = breakdown.find(
        (c) => c.criterionName === criterionName,
      );
      if (!criterion) return null;

      const score = Math.round(criterion.normalizedScore || 0);
      const weight = criterion.weight || 0;
      const weightedScore = Math.round(score * weight);
      const totalCount = criterion.totalCount || 0;
      const successCount = criterion.successCount || 0;
      const config = componentConfig[criterionName] || {
        name: criterionName,
        color: "#6b7280",
        formatLabel: () => `${score}%`,
      };

      return {
        name: config.name,
        color: config.color,
        score: score,
        weight: Math.round(weight * 100),
        weightedScore: weightedScore,
        label: config.formatLabel(totalCount, successCount),
      };
    })
    .filter(Boolean);

  // Calculate total weighted for bar proportions
  const totalWeighted = components.reduce((sum, c) => sum + c.weightedScore, 0);

  // Get short strength label for display next to score — encouraging language for 50+ audience
  const getStrengthLabel = (score) => {
    if (score >= 90)
      return {
        text: t("dashboard.safetyScore.excellent"),
        className: "strength-excellent",
      };
    if (score >= 75)
      return {
        text: t("dashboard.safetyScore.strong"),
        className: "strength-strong",
      };
    if (score >= 60)
      return {
        text: t("dashboard.safetyScore.good"),
        className: "strength-good",
      };
    if (score >= 40)
      return {
        text: t("dashboard.safetyScore.buildingUp"),
        className: "strength-fair",
      };
    if (score >= 20)
      return {
        text: t("dashboard.safetyScore.goodStart"),
        className: "strength-needs-work",
      };
    return {
      text: t("dashboard.safetyScore.justStarted"),
      className: "strength-critical",
    };
  };

  // Get explanation text based on score — plain-English, encouraging
  const getScoreExplanation = (score) => {
    if (score >= 80) {
      return t("dashboard.safetyScore.explain80");
    } else if (score >= 60) {
      return t("dashboard.safetyScore.explain60");
    } else if (score >= 40) {
      return t("dashboard.safetyScore.explain40");
    } else {
      return t("dashboard.safetyScore.explain0");
    }
  };

  // Get the single, clear next-action CTA based on score
  const getNextActionCTA = (score) => {
    if (score >= 90) return null; // No action needed
    if (score >= 60) return t("dashboard.safetyScore.cta60");
    return t("dashboard.safetyScore.cta0");
  };

  // Get the color for the circle gauge
  const getGaugeColor = (score) => {
    if (score >= 75) return "#10b981";
    if (score >= 50) return "#3b82f6";
    if (score >= 30) return "#f59e0b";
    return "#ef4444";
  };

  return (
    <div className="simple-card safety-score-card">
      <div className="safety-card-header">
        <h2 className="simple-card-title">
          {t("dashboard.safetyScore.title")}
        </h2>
        <RefreshButton
          onClick={handleRefresh}
          loading={isRefreshing}
          size="sm"
        />
      </div>

      {/* Main Score - Single Clear Circle Gauge */}
      <div className="shield-score-hero">
        <div className="shield-circle-gauge">
          <svg viewBox="0 0 120 120" className="shield-gauge-svg">
            {/* Background circle */}
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke="var(--bg-tertiary, #e5e7eb)"
              strokeWidth="10"
            />
            {/* Filled arc */}
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              stroke={getGaugeColor(totalScore)}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${(totalScore / 100) * 327} 327`}
              transform="rotate(-90 60 60)"
              style={{ transition: "stroke-dasharray 0.8s ease" }}
            />
          </svg>
          <div className="shield-gauge-center">
            <span className="shield-gauge-value">{totalScore}%</span>
          </div>
        </div>
        <div className="shield-hero-details">
          <span
            className={`shield-strength-label ${getStrengthLabel(totalScore).className}`}
          >
            {getStrengthLabel(totalScore).text}
          </span>
          <span className="shield-score-period">
            {t("dashboard.safetyScore.lastDays")}
          </span>
        </div>
      </div>

      {/* Score Explanation */}
      <p className="shield-explanation">{getScoreExplanation(totalScore)}</p>

      {/* CTA Button — plain-English next action (#2) */}
      {getNextActionCTA(totalScore) && (
        <div className="shield-cta-container">
          <button
            className="shield-cta-button"
            onClick={() => setHeroContent && setHeroContent("Training Center")}
            disabled={isReadOnlyView}
            title={
              isReadOnlyView ? "Not available in read-only view" : undefined
            }
          >
            {getNextActionCTA(totalScore)}
          </button>
        </div>
      )}

      {/* Collapsible score components breakdown */}
      <div className="shield-breakdown-collapsible">
        <button
          className="shield-breakdown-toggle"
          onClick={() => setBreakdownExpanded((prev) => !prev)}
          aria-expanded={breakdownExpanded}
        >
          {breakdownExpanded ? "▼" : "▶"}{" "}
          {t("dashboard.safetyScore.scoreComponents")}
        </button>
        {breakdownExpanded && (
          <div className="shield-simple-breakdown">
            {components.map((component) => (
              <div key={component.name} className="shield-breakdown-row">
                <span className="shield-breakdown-name">{component.name}</span>
                <span className="shield-breakdown-value">
                  {component.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SafetyScoreCard;
